// src/components/layouts/renderers/TripleSplitFrame.tsx
import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import type { LayoutRendererProps, TripleSplitConfig } from '@/lib/layouts/types';
import { clampFillerSpeed } from '@/lib/layouts/clampFillerSpeed';
import { resolveFillerPlaylist } from '@/lib/layouts/fillerPlaylist';
import { useLayerFont } from '@/hooks/use-layer-font';
import { FillerPlaylist, titleFrameStyles } from './SatisfatorioFrame';

const THIRD_PCT = Number((100 / 3).toFixed(4));

const band = (testId: string, children: React.ReactNode) => (
  <div
    data-testid={testId}
    style={{ height: `${THIRD_PCT}%`, position: 'relative', overflow: 'hidden', backgroundColor: '#0b0b0e' }}
  >
    {children}
  </div>
);

export const TripleSplitFrame: React.FC<LayoutRendererProps<TripleSplitConfig>> = ({ config, media }) => {
  const { width, height } = useVideoConfig();
  useLayerFont(config.titleFontFamily, config.titleFontUrl);
  const fillerSpeed = clampFillerSpeed(config.fillerSpeed);
  const hideTitles = config.titleFrame === 'none';
  const topTitle = hideTitles ? '' : config.title?.trim() ?? '';
  const bottomTitle = hideTitles ? '' : config.title2?.trim() ?? '';
  const regionW = width;
  const regionH = height / 3;
  const topPlaylist = resolveFillerPlaylist(config);
  const bottomPlaylist = resolveFillerPlaylist({
    fillerUrl: config.bottomFillerUrl,
    fillerUrls: config.bottomFillerUrls,
  });

  const fillerBand = (
    testId: string,
    slot: 'top' | 'bottom',
    urls: string[],
    fits?: TripleSplitConfig['fillerFits'],
    positions?: TripleSplitConfig['fillerPositions'],
  ) => band(
    testId,
    urls.length > 0 ? (
      <FillerPlaylist
        urls={urls}
        fillerSpeed={fillerSpeed}
        regionW={regionW}
        regionH={regionH}
        fits={fits}
        positions={positions}
        slot={slot}
      />
    ) : (
      <div
        data-testid="triple-split-filler-placeholder"
        style={{ width: '100%', height: '100%', backgroundColor: '#15161a' }}
      />
    ),
  );

  const headline = (text: string, testId: string, pos: number) => text && (
    <div
      data-testid={testId}
      data-autocut-over=""
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: `${pos}%`,
        transform: 'translateY(-50%)',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <span style={{
        ...titleFrameStyles(config.titleFrame, config.titleAccentColor || '#ED1C24'),
        ...(config.titleFontFamily ? { fontFamily: config.titleFontFamily } : {}),
        ...(config.titleFontSize ? { fontSize: config.titleFontSize } : {}),
      }}>
        {text}
      </span>
    </div>
  );

  return (
    <AbsoluteFill style={{ backgroundColor: '#000', display: 'flex', flexDirection: 'column' }}>
      {fillerBand('triple-split-filler-region-top', 'top', topPlaylist, config.fillerFits, config.fillerPositions)}
      <div
        data-testid="triple-split-clip-region"
        style={{ height: `${THIRD_PCT}%`, position: 'relative', overflow: 'hidden', backgroundColor: '#000' }}
      >
        {media}
      </div>
      {fillerBand(
        'triple-split-filler-region-bottom',
        'bottom',
        bottomPlaylist,
        config.bottomFillerFits,
        config.bottomFillerPositions,
      )}
      {headline(topTitle, 'triple-split-title', THIRD_PCT)}
      {headline(bottomTitle, 'triple-split-title-2', THIRD_PCT * 2)}
    </AbsoluteFill>
  );
};
